import { isAxiosError } from "axios";

/**
 * creditReportExceptions
 *
 * Mensajes de error (código -> español) del flujo BDC / NIP.
 *
 * Porta el mapa de `mb-app-express/src/js/exceptions/CreditReportException.ts`
 * (códigos upstream de mb_platform: 1000..2000, errores de campo 1101..1118,
 * 6050) y añade:
 *  - los códigos LOCALES del BFF docvault (BDC010/011/020/021/030),
 *  - los códigos de resolución/plataforma (3920..3999),
 *  - el código de auth del interceptor (`I000`) y el `0001` (requirement no
 *    encontrado).
 *
 * Incluye un helper `mapBdcAxiosError` que normaliza un error de axios cuyo
 * body puede ser `{ error, code }` (errores locales del BFF) o
 * `{ code, message }` (errores propagados de mb_platform) a un
 * `{ code, message }` apto para toasts.
 */

export const DEFAULT_CREDIT_REPORT_ERROR =
  "Algo salió mal, por favor inténtalo de nuevo más tarde.";

export const creditReportErrorMessages: Record<string, string> = {
  // --- Estado del reporte (mb_platform) ---
  "1000": "El reporte ya está completado",
  "1001": "Reporte sin historial crediticio",
  "1002": "Reporte deshabilitado o inválido",
  "1003": "Reporte bloqueado por Buró de Crédito",
  "1004": "El reporte no está en estado de validación de NIP",
  "1005": "El reporte no está en estado de captura de datos",
  "1006": "La investigación ya está completada",
  "1007": "El reporte no está en estado de reporte largo",
  "1008": "El reporte está siendo procesado",
  "1009": "El NIP es incorrecto.",
  "1010": "El NIP no ha sido confirmado",
  "1011": "El NIP no es válido",
  "1012": "Este NIP ya fue utilizado. Actualiza la página para revisar el resultado de la misma consulta. Si no cambia, contacta a quien te compartió el expediente; no reenvíes otro NIP.",
  "1013": "El NIP ha expirado, porfavor reenvia un nuevo NIP",
  "1014": "El NIP tiene demasiados reenvíos, crea un reporte nuevo",
  "1015": "El reporte ha sido invalidado",
  "1016": "Las cuotas no son suficientes para completar este reporte",
  "1017": "Reporte con coincidencias ambiguas por Buró de Crédito",
  "1018":
    "El historial crediticio es extremadamente largo, por favor contacta al equipo de soporte para poder ayudarte.",

  // --- Errores de campo del PUT (mb_platform) ---
  "1101": "El campo 'email' tiene un formato incorrecto.",
  "1102": "El campo 'Teléfono' tiene un formato incorrecto.",
  "1104": "El campo 'Nombre(s)' tiene un formato incorrecto.",
  "1105": "El campo 'Apellido Paterno' tiene un formato incorrecto.",
  "1106": "El campo 'Apellido Materno' tiene un formato incorrecto.",
  "1107": "El campo 'Fecha de Nacimiento' tiene un formato incorrecto.",
  "1108": "El campo 'RFC' tiene un formato incorrecto.",
  "1109": "El campo 'CURP' tiene un formato incorrecto.",
  "1111": "La 'dirección' tiene un formato incorrecto.",
  "1112": "El campo 'Colonia' tiene un formato incorrecto.",
  "1113": "El campo 'Municipio' tiene un formato incorrecto.",
  "1114": "El campo 'Estado' tiene un formato incorrecto.",
  "1116": "El campo 'Código Postal' tiene un formato incorrecto.",
  "1117": "El campo 'Ciudad' tiene un formato incorrecto.",
  "1118": "El campo 'Razón Social' tiene un formato incorrecto.",
  "1200": "El nombre excede el límite de Buró de Crédito (26 caracteres).",
  "1201":
    "El apellido paterno excede el límite de Buró de Crédito (26 caracteres).",
  "1202":
    "El apellido materno excede el límite de Buró de Crédito (26 caracteres).",
  "1203": "El RFC excede el límite de Buró de Crédito (13 caracteres).",
  "1204": "La CURP excede el límite de Buró de Crédito (18 caracteres).",
  "1205":
    "La razón social excede el límite de Buró de Crédito (75 caracteres).",

  "2000": "La investigación ha sido invalidada",

  // --- Resolución folio -> credit_report / plataforma (BFF docvault) ---
  "3920":
    "No fue posible localizar la investigación asociada a este expediente.",
  "3921": "No fue posible localizar el reporte de crédito de este expediente.",
  "3922": "El tipo de persona del reporte no es compatible.",
  "3923":
    "No se pudo conectar la autorización de Buró de Crédito. Contacta al equipo de soporte para revisar la conexión del servicio.",
  "3996":
    "Algo salió mal al comunicarnos con Buró de Crédito, inténtalo de nuevo.",
  "3997":
    "El servicio de Buró de Crédito tardó demasiado en responder, inténtalo de nuevo.",
  "3998":
    "Algo salió mal al comunicarnos con Buró de Crédito, inténtalo de nuevo.",
  "3999":
    "Ocurrió un error inesperado al procesar tu solicitud, inténtalo de nuevo.",

  // --- Errores LOCALES del BFF docvault ({ error, code }) ---
  BDC010: "Este requisito no corresponde a la autorización de Buró de Crédito.",
  BDC011: "Este expediente no tiene un folio asignado para iniciar el proceso.",
  BDC012:
    "Esta autorización de Buró de Crédito pertenece a otro participante del expediente.",
  BDC013:
    "La autorización nativa de Buró de Crédito sólo está disponible para el participante del expediente.",
  BDC014:
    "La autorización nativa de Buró sólo está disponible para el inquilino o fiador principal.",
  BDC020: "El NIP y su confirmación son obligatorios.",
  BDC021: "El NIP y su confirmación no coinciden.",
  BDC022: "El NIP debe contener cuatro dígitos.",
  BDC030: "El método de reenvío no es válido. Usa Email o SMS.",
  BDC040:
    "Necesitas aceptar los términos y el consentimiento de medios electrónicos para continuar.",
  BDC050:
    "La autorización de Buró de Crédito no coincide con el tipo de expediente. Contacta al equipo de soporte para regenerar la investigación correcta.",
  BDC070:
    "Completa los pasos anteriores antes de iniciar la autorización de Buró de Crédito.",

  // --- Otros ---
  "0001": "No encontramos el requisito solicitado.",
  I000: "Tu sesión no es válida o no tienes acceso a este expediente.",

  // general error (paridad con express)
  "6050": DEFAULT_CREDIT_REPORT_ERROR,
};

export const NON_RETRYABLE_BDC_STATUS_CODES = new Set([
  "I000",
  "3920",
  "3921",
  "3922",
  "3923",
  "BDC096",
  "BDC050",
  "BDC012",
  "BDC013",
  "BDC014",
]);

export function isDeterministicBdcError(
  code: string | null | undefined,
): boolean {
  return Boolean(code && NON_RETRYABLE_BDC_STATUS_CODES.has(code));
}

/**
 * Devuelve el mensaje en español para un `code` conocido, o `undefined` si no
 * está mapeado (para permitir fallback al mensaje del servidor).
 */
export function lookupCreditReportMessage(
  code: string | null | undefined,
): string | undefined {
  if (!code) return undefined;
  return creditReportErrorMessages[code];
}

export interface BdcErrorInfo {
  /** Código crudo del backend (local `BDCxxx`/`0001`/`I000` o upstream `1xxx`/`3xxx`). */
  code: string | null;
  /** Mensaje listo para mostrar en un toast. */
  message: string;
  recoverable?: boolean;
  fields?: BdcRecoverableField[];
  /** Field details that may include issues without a participant-editable target. */
  fieldErrors?: Record<string, string>;
}

export interface BdcRecoverableField {
  field: string;
  target_slug: string;
  label: string;
}

interface BdcErrorBody {
  code?: string | number | null;
  backendCode?: string;
  /** Mensaje upstream (mb_platform): body `{ code, message }`. */
  message?: unknown;
  /** Mensaje local del BFF docvault: body `{ error, code }`. */
  error?: unknown;
  recoverable?: unknown;
  fields?: unknown;
  field_errors?: unknown;
  blockers?: unknown;
}

/**
 * Normaliza un error de axios (o un body de respuesta) del flujo BDC a
 * `{ code, message }`.
 *
 * Reglas de mensaje (en orden de preferencia):
 *  1. Para errores recoverable, mensaje del servidor (contrato BDC field errors).
 *  2. Mensaje en español mapeado por `code` (catálogo local).
 *  3. Mensaje del servidor (`message` upstream o `error` local).
 *  4. Mensaje por defecto.
 *
 * Acepta tanto el error de axios completo (`error.response.data`) como un body
 * suelto (útil para el caso "HTTP 200 con `code` de error en el body").
 */
export function mapBdcAxiosError(error: unknown): BdcErrorInfo {
  const body = extractBdcErrorBody(error);
  const rawCode =
    body?.code === "I000" ? (body.backendCode ?? body.code) : body?.code;
  const code =
    rawCode !== undefined && rawCode !== null ? String(rawCode) : null;

  const serverMessage = normalizeServerMessage(body?.message ?? body?.error);
  const blockers = normalizeRecoverableFields(body?.blockers, true);
  const fields = [...normalizeRecoverableFields(body?.fields), ...blockers];
  const recoverable = body?.recoverable === true || blockers.length > 0;
  const fieldErrors = normalizeFieldErrors(body?.field_errors);
  const message =
    (recoverable ? serverMessage : undefined) ??
    lookupCreditReportMessage(code) ??
    serverMessage ??
    DEFAULT_CREDIT_REPORT_ERROR;

  return {
    code,
    message,
    ...(recoverable ? { recoverable: true, fields } : {}),
    ...(Object.keys(fieldErrors).length > 0 ? { fieldErrors } : {}),
  };
}

function normalizeFieldErrors(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const result: Record<string, string> = {};
  for (const [field, rawMessage] of Object.entries(value)) {
    const message = Array.isArray(rawMessage)
      ? rawMessage
          .filter((item): item is string => typeof item === "string")
          .map((item) => item.trim())
          .filter(Boolean)
          .join(" ")
      : typeof rawMessage === "string"
        ? rawMessage.trim()
        : "";
    if (field.trim() && message) result[field] = message;
  }
  return result;
}

function normalizeRecoverableFields(
  value: unknown,
  isBlocker = false,
): BdcRecoverableField[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item): BdcRecoverableField[] => {
    if (!item || typeof item !== "object") return [];

    const record = item as Record<string, unknown>;
    const field = normalizeFieldText(isBlocker ? record.key : record.field);
    const targetSlug = normalizeFieldText(record.target_slug);
    if (!field || !targetSlug) return [];

    return [
      {
        field,
        target_slug: targetSlug,
        label:
          normalizeFieldText(isBlocker ? record.message : record.label) ??
          field,
      },
    ];
  });
}

function normalizeFieldText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeServerMessage(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return undefined;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  const validationSummary = summarizeStructuredServerMessage(value);
  if (validationSummary) return validationSummary;
  try {
    return JSON.stringify(value);
  } catch {
    return DEFAULT_CREDIT_REPORT_ERROR;
  }
}

const validationFieldLabels: Record<string, string> = {
  legal_entity_name: "razón social",
  rfc: "RFC",
  street: "calle",
  exterior_number: "número exterior",
  zip_code: "código postal",
  neighborhood: "colonia",
  municipality: "municipio",
  city: "ciudad",
  state: "estado",
};

function summarizeStructuredServerMessage(value: unknown): string | undefined {
  const labels: string[] = [];

  const visit = (node: unknown) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) return;
    for (const [key, child] of Object.entries(node)) {
      if (Array.isArray(child) && child.length > 0) {
        labels.push(validationFieldLabels[key] ?? key);
      } else {
        visit(child);
      }
    }
  };

  visit(value);
  const uniqueLabels = [...new Set(labels)];
  if (uniqueLabels.length === 0) return undefined;
  return `Faltan o son inválidos estos datos para iniciar Buró de Crédito: ${uniqueLabels.join(", ")}.`;
}

function extractBdcErrorBody(error: unknown): BdcErrorBody | undefined {
  if (!error || typeof error !== "object") return undefined;

  const maybeAxios = error as {
    response?: { data?: unknown };
    code?: unknown;
    message?: unknown;
    error?: unknown;
  };

  // Caso 1: error de axios -> usar response.data si trae code/message/error.
  const data = maybeAxios.response?.data;
  if (data && typeof data === "object") {
    return data as BdcErrorBody;
  }

  // Axios transport codes/messages are client diagnostics, not a backend body.
  if (isAxiosError(error)) return undefined;

  // Caso 2: body suelto pasado directamente (p.ej. 200 con error en el body).
  // Solo si trae la firma de un body del backend (`code`/`error`); un Error de
  // JS genérico (solo `message`) NO debe tratarse como body del servidor.
  if ("code" in maybeAxios || "error" in maybeAxios) {
    return maybeAxios as BdcErrorBody;
  }

  return undefined;
}
